import {AxiosResponse} from 'axios';
import {Workspace} from '../components/pages/user-management/models';
import {IFiltersData} from '../components/pages/WelcomeScreenSingleClient';
import config from '../config';
import {
  TABLE_PER_PAGE_5000_ROW,
  PERMISSION_GROUP_CATEGORY,
  TABLE_DEFAULT_PAGE_NUMBER,
} from '../constants/constants';
import {UserRolesData} from '../redux/slices/userRoleSlice';
import {
  AddNewOrUpdateProjectsToUserRoleReq,
  AlertReuqest,
  ClientManagementApiResult,
  CreatePermissionGroupRequest,
  CreateUserRoleRequest,
  DeleteUserRoleCommandResponse,
  DeleteUserRoleRequest,
  PermissionGroupRequest,
  resolveClientManagementCommandDtoFromAxios,
  UpdateUserRoleRequest,
} from '../types/client-management/cientManagementTypes';
import {
  AddNewClientDepartmentReq,
  UpdateClientDepartmentReq,
} from '../types/client-management/departmentTypes';
import {PermissionGroup} from '../types/permission-group/permissionGroup';
import {ProjectFields} from '../types/project-type/projectDetailsType';
import {ClientData, ProjectGroup} from '../types/types';
import {Role} from '../types/user-roles/roles';
import apiClient from './index';

// Other client management functions can be added similarly...
const clientManagementEndpoint = (path: string) =>
  `/${config.API_VERSION}/clientmanagement${path}`;

const selectiveSelfServiceEndpoint = (path: string) =>
  `/${config.API_VERSION}/selectiveselfmanagement${path}`;

export const getClientById = (clientId: string, signal?: AbortSignal) => {
  return apiClient.get(clientManagementEndpoint(`/clients/${clientId}`), {
    signal: signal,
  });
};

export const getPermissionsbyClientId = (clientId: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/userpermissions?clientId=${clientId}`)
  );
};

export const getPermissionsbyProjectId = (
  clientId: string,
  projectId: string
) => {
  const params = {
    clientId,
    projectId,
  };

  return apiClient.get(clientManagementEndpoint(`/userpermissions`), {params});
};

export const getClientsList = (params?: string) => {
  let url = '/clients';
  if (params) {
    url = `${url}${params}`;
  }
  return apiClient.get(clientManagementEndpoint(url));
};

export const getClientsAsLookup = async () => {
  // const queryString = '?pageNumber=1&pageSize=1000000';
  // Commented above for now as its freezing all related screen, please have a look
  const queryString = '?pageNumber=1&pageSize=25';
  const clients = await getClientsList(queryString);
  const response = {
    data: clients?.data?.data?.map((client: ClientData) => {
      return {id: client.clientId, name: client.name};
    }),
  };
  return response;
};

const getProjectsList = (clientId: string, ODataQuery?: string) => {
  const query = ODataQuery ? `?${ODataQuery}` : '';
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/projects${query}`)
  );
};

export const getProjectsAsLookup = async (
  clientId: string,
  ODataQuery?: string
) => {
  const projects = await getProjectsList(clientId, ODataQuery);
  const response = {
    data: projects?.data?.data?.data?.map((project: ProjectGroup) => {
      return {id: project.projectId.toLowerCase(), name: project.projectName};
    }),
  };
  return response;
};

export const searchProjects = (params?: string) => {
  let url = '/projects';
  if (params) {
    url = `${url}${params}`;
  }
  return apiClient.get(clientManagementEndpoint(url));
};

export const createUserRole = async (
  userId: string,
  data: CreateUserRoleRequest | {roleId: string | undefined}
): Promise<UserRolesData> => {
  const response = await apiClient.post<{
    data?: ClientManagementApiResult<UserRolesData> | UserRolesData;
  }>(clientManagementEndpoint(`/users/${userId}/userroles`), data);

  const dto = resolveClientManagementCommandDtoFromAxios<UserRolesData>(
    response.data
  );
  if (!dto?.userRoleId) {
    throw new Error('Create user role response missing userRoleId');
  }
  return dto;
};

export const deleteUserRole = async (
  userId: string,
  userRoleId: string,
  data?: DeleteUserRoleRequest
): Promise<DeleteUserRoleCommandResponse> => {
  const response = await apiClient.delete<{
    data?:
      | ClientManagementApiResult<DeleteUserRoleCommandResponse>
      | DeleteUserRoleCommandResponse;
  }>(clientManagementEndpoint(`/users/${userId}/userroles/${userRoleId}`), {
    data,
  });

  const dto =
    resolveClientManagementCommandDtoFromAxios<DeleteUserRoleCommandResponse>(
      response.data
    );

  return {
    userRoleId: String(dto?.userRoleId ?? userRoleId),
  };
};

export const updateUserRole = async (
  userRoleId: string,
  data: UpdateUserRoleRequest
): Promise<UserRolesData> => {
  const response = await apiClient.put<{
    data?: ClientManagementApiResult<UserRolesData> | UserRolesData;
  }>(clientManagementEndpoint(`/users/userroles/${userRoleId}`), data);

  const dto = resolveClientManagementCommandDtoFromAxios<UserRolesData>(
    response.data
  );
  if (!dto?.userRoleId) {
    throw new Error('Update user role response missing userRoleId');
  }
  return dto;
};

export const getRolesList = () => {
  return apiClient.get(clientManagementEndpoint('/roles'));
};

export const getRolesAsLookup = async () => {
  const roles = await getRolesList();
  const response = {
    data: roles?.data?.data?.value?.roles?.map((role: Role) => {
      return {id: role.roleId, name: role.name, roleCode: role.roleCode};
    }),
  };
  return response;
};

export const getPermissionsList = () => {
  return apiClient.get(clientManagementEndpoint('/permissions'));
};

export const createPermissionGroup = (
  clientId: string,
  data: CreatePermissionGroupRequest
) => {
  return apiClient.post(
    clientManagementEndpoint(`/clients/${clientId}/permissiongroups`),
    data
  );
};

export const getPermissionGroupList = (
  clientId: string,
  category?: PERMISSION_GROUP_CATEGORY,
  search?: string
) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/permissiongroups`),
    {
      params: {
        category,
        search,
      },
    }
  );
};

export const getPermissionGroupsAsLookup = async (
  clientId: string,
  category?: PERMISSION_GROUP_CATEGORY
) => {
  const groups = await getPermissionGroupList(clientId, category);
  const response = {
    data: groups?.data?.data?.permissionGroups?.map(
      (group: PermissionGroup) => {
        return {id: group.permissionGroupId, name: group.name};
      }
    ),
  };
  return response;
};

export const searchPermissionGroups = (params?: string) => {
  let url = '/permissiongroups';
  if (params) {
    url = `${url}${params}`;
  }
  return apiClient.get(clientManagementEndpoint(url));
};

export const getAlertsList = (clientId: string) => {
  return apiClient.get(clientManagementEndpoint(`/alerts/${clientId}`));
};
export const updateAlertsList = (
  clientId: string,
  updateObj: AlertReuqest[]
) => {
  return apiClient.put(
    clientManagementEndpoint(`/alerts/${clientId}`),
    updateObj
  );
};

export const deletePermissionGroup = (
  clientId: string,
  permissionGroupId: string
) => {
  return apiClient.delete(
    clientManagementEndpoint(
      `/clients/${clientId}/permissiongroups/${permissionGroupId}`
    )
  );
};

export const updatePermissionGroup = async (
  clientId: string,
  permissionGroupId: string,
  data: PermissionGroupRequest
) => {
  return apiClient.put(
    clientManagementEndpoint(
      `/clients/${clientId}/permissiongroups/${permissionGroupId}`
    ),
    data
  );
};

export const getPermissionGroupUsers = (
  clientId: string,
  permissionGroupId: string
) => {
  return apiClient.get(
    clientManagementEndpoint(
      `/clients/${clientId}/permissiongroups/${permissionGroupId}`
    )
  );
};

export const getProjectsListXLSX = (clientId: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/projects/export`),
    {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    }
  );
};

export const getProjectByProjectId = (
  projectId: string,
  signal?: AbortSignal
) => {
  return apiClient.get(clientManagementEndpoint(`/projects/${projectId}`), {
    signal: signal,
  });
};
export const getDepartmentList = (clientId: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/departments`)
  );
};
export const getBranchList = (clientId: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/branches`)
  );
};

export const getProjectsByClientId = (clientId: string) => {
  return apiClient.post(
    clientManagementEndpoint(
      `/clients/${clientId}/projects/me?pageSize=${TABLE_PER_PAGE_5000_ROW}&pageNumber=${TABLE_DEFAULT_PAGE_NUMBER}`
    ),
    {}
  );
};

export const updateClientDepartment = (
  departmentData: UpdateClientDepartmentReq
) => {
  return apiClient.put(clientManagementEndpoint(`/department`), departmentData);
};
export const deleteClientDepartment = (departmentData: {
  data: {clientId: string; departmentId: string};
}) => {
  return apiClient.delete(
    clientManagementEndpoint(`/department`),
    departmentData
  );
};
export const addNewClientDepartment = (
  departmentData: AddNewClientDepartmentReq
) => {
  return apiClient.post(
    clientManagementEndpoint(`/department`),
    departmentData
  );
};

export const updateProjectByProjectId = (
  projectId: string,
  projectData: ProjectFields
) => {
  return apiClient.patch(
    clientManagementEndpoint(`/projects/${projectId}`),
    projectData
  );
};

export const getDepartmentsByBranchId = (branchId: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/branches/${branchId}/departments`)
  );
};

export const getUserRoles = (userId: string) => {
  return apiClient.get(clientManagementEndpoint(`/users/${userId}/userroles`));
};

export const getGroupMembers = (params?: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/eca/projects/users?` + params)
  );
};
export const getFilteredProjectList = (
  clientId: string,
  ODataQuery: string
) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/projects${ODataQuery}`)
  );
};

export const getActiveProjectList = (clientId: string) => {
  return getFilteredProjectList(clientId, `?$filter=status ne 'closed'`);
};

export const getHighlyConfidentialProjectsList = (clientId: string) => {
  return getFilteredProjectList(
    clientId,
    `?$filter=status ne 'closed' and highlyConfidential eq true`
  );
};

export const getProjectList = (ODataQuery: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/projects/project-list${ODataQuery}`)
  );
};

export const exportProjectListXlsx = (ODataQuery: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/projects/project-list/export/xlsx${ODataQuery}`),
    {
      responseType: 'blob',
      headers: {
        Accept:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    }
  );
};

const getWorkspaces = (clientId: string, projectId: string) => {
  return apiClient.get(
    clientManagementEndpoint(
      `/clients/${clientId}/projects/${projectId}/workspaces`
    )
  );
};

export const getProjectAdded = (params?: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/projects/lookup?` + (params ?? ''))
  );
};
export const getEcaProjectsList = (params?: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/projects/eca/lookup` + (params ?? ''))
  );
};

export const getWorkspacesAsLookup = async (
  clientId: string,
  projectId: string
) => {
  const workspaces = await getWorkspaces(clientId, projectId);
  const response = {
    data: workspaces?.data?.data?.map((workspace: Workspace) => {
      return {id: workspace.workspaceId, name: workspace.workspaceName};
    }),
  };
  return response;
};
export const getSavedFilters = (params: string) => {
  return apiClient.get(clientManagementEndpoint(`/filters/${params}`));
};

export const clearFilters = (params: string) => {
  return apiClient.delete(clientManagementEndpoint(`/filters/${params}`));
};

export const saveFilters = (
  params: string,
  requestBody?: IFiltersData | Record<string, unknown>
) => {
  return apiClient.post(
    clientManagementEndpoint(`/clients/filters/${params}`),
    requestBody
  );
};

export const addNewOrUpdateProjectsToUserRole = (
  userRoleId: string,
  data:
    | AddNewOrUpdateProjectsToUserRoleReq
    | AddNewOrUpdateProjectsToUserRoleReq[]
) => {
  return apiClient.post(
    clientManagementEndpoint(`/users/userroles/${userRoleId}/projects`),
    data
  );
};

export const getUserListByClientIdAndProjectId = (
  clientId: string,
  projectId: string
) => {
  const endpoint = clientManagementEndpoint('/projects/users');
  const params = {
    clientId,
    projectId,
  };
  return apiClient.get(endpoint, {params});
};

export const getLaunchPointUrl = (instanceId: string, workspaceId: string) => {
  const endpoint = clientManagementEndpoint('/clients/LaunchPoint');
  const params = {
    instanceId,
    workspaceId,
  };
  return apiClient.get(endpoint, {params});
};

export const downloadProjectsDetailsExportXlsx = (
  clientId: string,
  status: string,
  requestBody: IFiltersData | Record<string, unknown>
): Promise<AxiosResponse<Blob>> => {
  const endpoint = clientManagementEndpoint(
    `/clients/${clientId}/projects/me/export/xlsx`
  );

  const {search = '', ...body} = requestBody as IFiltersData;

  return apiClient.post<Blob>(endpoint, body, {
    responseType: 'blob',
    headers: {
      Accept:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    },
    params: {
      search: search,
      status: status,
    },
  });
};

export const getProjectRealtimeBudget = (
  clientId: string,
  projectId: string
) => {
  const endpoint = selectiveSelfServiceEndpoint(
    `/clients/${clientId}/projects/${projectId}/budget-detail`
  );
  return apiClient.get(endpoint);
};

export const updateBudgetConsumption = (
  clientId: string,
  projectId: string,
  budgetConsumptionEnabled: boolean
) => {
  const params = {
    budgetConsumptionEnabled,
  };
  const endpoint = clientManagementEndpoint(
    `/clients/${clientId}/project/${projectId}/budgetconsumption`
  );
  return apiClient.post(endpoint, params);
};

export const budgetViewProjects = (params?: string) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/projects/pageinfo${params}`)
  );
};

export const getDomainWhitelist = (clientId: string) => {
  const endpoint = clientManagementEndpoint(`/domains/${clientId}`);
  return apiClient.get(endpoint);
};

export const createDomainWhitelist = async (
  clientId: string,
  domainName: string
) => {
  try {
    const endpoint = clientManagementEndpoint(
      `/domains/${clientId}?domain=${domainName}`
    );
    const response = await apiClient.post(endpoint);
    return response.data;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const updateDomainWhitelist = async (
  clietnDomainId: string,
  domain: string,
  clientId: string
) => {
  return apiClient.put(
    clientManagementEndpoint(`/domains/${clietnDomainId}?domain=${domain}`),
    {clientId}
  );
};

export const deleteDomainWhitelist = (
  clientDomainId: string,
  clientId: string
) => {
  return apiClient.delete(
    clientManagementEndpoint(`/domains/${clientDomainId}`),
    {data: {clientId}}
  );
};

export const validateProjectName = (
  clientId: string,
  engagementCode: string,
  projectName: string
) => {
  const params = {
    clientId,
    engagementCode,
    projectName,
  };

  return apiClient.get(clientManagementEndpoint(`/project/validate`), {
    params,
  });
};

export const getBillingEngagementsList = (clientId: string) => {
  return apiClient.get(
    clientManagementEndpoint(
      `/project/billing-engagements?clientId=${clientId}`
    )
  );
};

export const getNewPendingProjects = (
  clientId: string,
  params: {
    page: number;
    pageSize: number;
  }
) => {
  return apiClient.get(
    clientManagementEndpoint(`/clients/${clientId}/new-pending-projects`),
    {
      params,
    }
  );
};
export const dismissNewProjects = async (
  clientId: string,
  projectId: string
) => {
  return apiClient.put(
    clientManagementEndpoint(
      `/newproject/dismiss?clientId=${clientId}&projectId=${projectId}`
    )
  );
};
